/**
 * Wiring RUNS — the unit a user actually thinks in.
 *
 * `room.electrical.connections` stores one hop at a time
 * (`{ switchId, fixtureId }`), and the Wires tool lays a chain: switch →
 * light1 → light2 … Nobody wants to delete "the second hop"; they want the run
 * gone. So a run here is the CONNECTED COMPONENT of the hop graph — walked
 * undirected, because which end of a hop is "upstream" is an artefact of the
 * order the user clicked, not something they can see on the plan.
 *
 * Hops live on the downstream fixture's room and a run can cross rooms, so
 * every room is folded into one graph before components are taken, and each
 * hop remembers the room it must be deleted from.
 *
 * Pure: no React, no canvas, no Firestore.
 */

import {
  wirePolyline,
  connectionIsOnScreen,
  runBowAnchor,
} from './electricalDrawingUtils';

/** How close a click has to land, in SCREEN pixels, to pick a run. */
export const WIRE_HIT_TOLERANCE_PX = 12;

/**
 * Every hop in the structure, each tagged with the room that owns it.
 *
 * @param {Array} rooms - Rooms with `electrical.connections`
 * @returns {Array<{id: string, switchId: string, fixtureId: string, roomId: string}>}
 */
export const collectConnections = (rooms) => {
  const out = [];
  (rooms || []).forEach((room) => {
    (room?.electrical?.connections || []).forEach((connection, index) => {
      if (!connection?.switchId || !connection?.fixtureId) return;
      out.push({
        // A connection written before ids were stamped falls back to its
        // position, which is also what `resolveWireSegments` keys on.
        id: connection.id || `${room.id}-${index}`,
        switchId: connection.switchId,
        fixtureId: connection.fixtureId,
        roomId: room.id,
      });
    });
  });
  return out;
};

/**
 * Group hops into runs.
 *
 * @param {Array} rooms - Rooms with `electrical.connections`
 * @returns {Array<{id: string, connections: Array, objectIds: Set<string>}>}
 *   Runs in first-hop order; `id` is the run's first hop id, so it is stable
 *   as long as that hop lives.
 */
export const buildWiringRuns = (rooms) => {
  const connections = collectConnections(rooms);
  if (!connections.length) return [];

  // Undirected adjacency over object ids.
  const neighbours = new Map();
  const link = (a, b) => {
    if (!neighbours.has(a)) neighbours.set(a, new Set());
    neighbours.get(a).add(b);
  };
  connections.forEach((c) => {
    link(c.switchId, c.fixtureId);
    link(c.fixtureId, c.switchId);
  });

  // Flood each component, then collect the hops whose endpoints are in it.
  const componentOf = new Map();
  let componentIndex = 0;
  for (const start of neighbours.keys()) {
    if (componentOf.has(start)) continue;
    const stack = [start];
    componentOf.set(start, componentIndex);
    while (stack.length) {
      const id = stack.pop();
      for (const next of neighbours.get(id) || []) {
        if (componentOf.has(next)) continue;
        componentOf.set(next, componentIndex);
        stack.push(next);
      }
    }
    componentIndex++;
  }

  const runs = [];
  const byComponent = new Map();
  connections.forEach((c) => {
    const key = componentOf.get(c.switchId);
    let run = byComponent.get(key);
    if (!run) {
      run = { id: c.id, connections: [], objectIds: new Set() };
      byComponent.set(key, run);
      runs.push(run);
    }
    run.connections.push(c);
    run.objectIds.add(c.switchId);
    run.objectIds.add(c.fixtureId);
  });
  return runs;
};

/** Squared distance from p to segment ab. */
const distanceSqToSegment = (p, a, b) => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  let t = 0;
  if (lenSq > 0) {
    t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq;
    t = Math.max(0, Math.min(1, t));
  }
  const dx = p.x - (a.x + abx * t);
  const dy = p.y - (a.y + aby * t);
  return dx * dx + dy * dy;
};

/**
 * The run a click lands on, or null.
 *
 * Measured against the SAME polyline the wire is drawn as (`wirePolyline`), in
 * screen pixels — so the band is the width it looks, at any zoom, and a wire
 * whose endpoints are gone is not clickable because it is not drawn.
 *
 * @param {Object} params
 * @param {Array} params.runs - From `buildWiringRuns`
 * @param {Map<string, {x: number, y: number}>} params.screenPositions - Object
 *   id → its screen position (the same map the overlay draws from)
 * @param {{x: number, y: number}} params.point - Click, screen px
 * @param {number} [params.tolerancePx]
 * @param {Set<string>|null} [params.visibleIds] - What is on screen. Uses the
 *   SAME rule the overlay draws by, so a wire is never drawn-but-unclickable
 *   or clickable-but-invisible.
 * @returns {Object|null} The nearest run within tolerance
 */
export const runAtPoint = ({
  runs,
  screenPositions,
  scale = null,
  point,
  tolerancePx = WIRE_HIT_TOLERANCE_PX,
  visibleIds = null,
}) => {
  if (!runs?.length || !screenPositions || !point) return null;
  const maxSq = tolerancePx * tolerancePx;
  let best = null;
  let bestSq = Infinity;

  runs.forEach((run) => {
    // The same anchor the draw bows away from, or the clickable band sits off
    // the line the eye sees.
    const away = runBowAnchor(run, screenPositions);
    run.connections.forEach((c) => {
      const from = screenPositions.get(c.switchId);
      const to = screenPositions.get(c.fixtureId);
      if (!from || !to) return;
      if (!connectionIsOnScreen(c, visibleIds)) return;
      // The SAME curve the eye sees — including a shape the user dragged in,
      // or the reshaped wire stops being clickable along its new line.
      const shape =
        c.shape && Number.isFinite(scale) ? { override: c.shape, scale } : null;
      const poly = wirePolyline(from.x, from.y, to.x, to.y, 24, away, shape);
      for (let i = 1; i < poly.length; i++) {
        const dSq = distanceSqToSegment(point, poly[i - 1], poly[i]);
        if (dSq < bestSq) {
          bestSq = dSq;
          best = run;
        }
      }
    });
  });

  return bestSq <= maxSq ? best : null;
};

/**
 * Drop every hop of a run from the rooms that own them.
 *
 * Returns only the rooms that actually changed, so a caller can write exactly
 * those — a run crossing three rooms is three writes, and a run in one room is
 * one, never a whole-structure rewrite.
 *
 * @param {Object} run - From `buildWiringRuns`
 * @returns {Map<string, Set<string>>} roomId → the connection ids to remove
 */
export const runRemovalsByRoom = (run) => {
  const byRoom = new Map();
  (run?.connections || []).forEach((c) => {
    if (!byRoom.has(c.roomId)) byRoom.set(c.roomId, new Set());
    byRoom.get(c.roomId).add(c.id);
  });
  return byRoom;
};

/**
 * A run's human label, for the delete affordance: the hop count, since a run
 * has no name and its switch may be off-screen.
 *
 * @param {Object} run
 * @returns {string}
 */
export const describeRun = (run) => {
  const hops = run?.connections?.length || 0;
  const fixtures = Math.max(0, (run?.objectIds?.size || 0) - 1);
  return `${hops} wire${hops === 1 ? '' : 's'} · ${fixtures} fixture${
    fixtures === 1 ? '' : 's'
  }`;
};

/**
 * A room with one connection's hand-drawn shape set — or cleared, for `null`.
 * PURE: returns a new room and never writes into the one it is handed, since
 * the auto-save diffs the returned room against the store's copy and an
 * in-place edit reads as no change.
 *
 * @param {Object} room
 * @param {string} connectionId
 * @param {{along: number, perp: number}|null} shape
 * @returns {Object} the room, unchanged when it does not own the connection.
 */
export const setConnectionShape = (room, connectionId, shape) => {
  const connections = room?.electrical?.connections;
  if (!Array.isArray(connections)) return room;
  if (!connections.some((c) => c?.id === connectionId)) return room;
  return {
    ...room,
    electrical: {
      ...room.electrical,
      connections: connections.map((c) => {
        if (c?.id !== connectionId) return c;
        if (!shape) {
          // eslint-disable-next-line no-unused-vars
          const { shape: dropped, ...rest } = c;
          return rest;
        }
        return { ...c, shape: { along: shape.along, perp: shape.perp } };
      }),
    },
  };
};

/** The room that owns a connection, or null. */
export const roomOwningConnection = (rooms, connectionId) =>
  (rooms || []).find((room) =>
    (room?.electrical?.connections || []).some((c) => c?.id === connectionId)
  ) || null;
